import { Empty, Input } from 'antd';
import type { TabsProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetOcppConfigurationJsonPanel } from '../components/AssetOcppConfigurationJsonPanel/AssetOcppConfigurationJsonPanel';
import type {
  AssetOcppConfigurationKeyRow,
  AssetOcppConfigurationSnapshot,
} from '../types';
import { useAssetOcppConfigurationColumns } from './useAssetOcppConfigurationColumns';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type UseAssetOcppConfigurationViewTabsInput = {
  rows: AssetOcppConfigurationKeyRow[];
  snapshot: AssetOcppConfigurationSnapshot | null;
  search: string;
  onSearchChange: (value: string) => void;
};

export function useAssetOcppConfigurationViewTabs({
  rows,
  snapshot,
  search,
  onSearchChange,
}: UseAssetOcppConfigurationViewTabsInput): TabsProps['items'] {
  const { t } = useTranslation('assets');
  const columns = useAssetOcppConfigurationColumns();

  return useMemo(
    () => [
      {
        key: 'table',
        label: t('detail.ocppConfiguration.views.table', 'Table'),
        children: (
          <>
            <Input.Search
              allowClear
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={t(
                'detail.ocppConfiguration.searchPlaceholder',
                'Search keys or values',
              )}
              style={{ marginBottom: 12 }}
            />
            <EvimoTable
              rowKey={(row) => row.key}
              columns={columns}
              dataSource={rows}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    description={t(
                      'detail.ocppConfiguration.empty',
                      'No configuration keys returned by the charger',
                    )}
                  />
                ),
              }}
              scroll={{ x: true, y: 480 }}
            />
          </>
        ),
      },
      {
        key: 'json',
        label: t('detail.ocppConfiguration.views.json', 'JSON'),
        children: <AssetOcppConfigurationJsonPanel snapshot={snapshot} />,
      },
    ],
    [columns, onSearchChange, rows, search, snapshot, t],
  );
}
